import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

interface GoalProgressRingSignature {
  Args: {
    percent: number;
    size?: number;
    strokeWidth?: number;
    trackClass?: string;
    progressClass?: string;
    labelClass?: string;
  };
  Element: HTMLDivElement;
}

export default class GoalProgressRing extends Component<GoalProgressRingSignature> {
  get size(): number {
    return this.args.size ?? 72;
  }

  get strokeWidth(): number {
    return this.args.strokeWidth ?? 6;
  }

  get radius(): number {
    return (this.size - this.strokeWidth) / 2;
  }

  @cached
  get circumference(): number {
    return 2 * Math.PI * this.radius;
  }

  @cached
  get dashOffset(): number {
    return this.circumference * (1 - this.args.percent / 100);
  }

  get trackClass(): string {
    return this.args.trackClass ?? 'll-chart-track';
  }

  get progressClass(): string {
    return this.args.progressClass ?? 'text-primary';
  }

  get labelClass(): string {
    return this.args.labelClass ?? 'fw-bold small';
  }

  get center(): number {
    return this.size / 2;
  }

  get rotateTransform(): string {
    return `rotate(-90 ${this.center} ${this.center})`;
  }

  <template>
    <div class="position-relative d-inline-flex flex-shrink-0" ...attributes>
      <span class="visually-hidden">Goal progress:
        {{@percent}}
        percent</span>
      <svg
        width={{this.size}}
        height={{this.size}}
        viewBox="0 0 {{this.size}} {{this.size}}"
        aria-hidden="true"
      >
        <circle
          class={{this.trackClass}}
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          fill="none"
          stroke="currentColor"
          stroke-width={{this.strokeWidth}}
        />
        <circle
          class={{this.progressClass}}
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          fill="none"
          stroke="currentColor"
          stroke-width={{this.strokeWidth}}
          stroke-dasharray={{this.circumference}}
          stroke-dashoffset={{this.dashOffset}}
          stroke-linecap="round"
          transform={{this.rotateTransform}}
        />
      </svg>
      <span
        class="position-absolute top-50 start-50 translate-middle mb-0
          {{this.labelClass}}
          text-body-emphasis"
      >{{@percent}}%</span>
    </div>
  </template>
}
